import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, AlertCircle, CheckCircle, Shield, Award, Users } from 'lucide-react';
import LLSEOMeta from './LLSEOMeta';

const LLContactPage = () => {
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: ''
  });

  // Loading and error states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({
    type: '', // 'success' or 'error'
    message: ''
  });

  // Schema markup for Local Business + Attorney
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LegalService",
        "@id": "https://loopholelaw.in/#legalservice",
        "name": "LoopHoleLaw - Best Lawyers in Kollam",
        "image": "https://loopholelaw.in/logo.png",
        "description": "Leading legal services in Kollam, Kerala. Expert lawyers specializing in Criminal Law, Corporate Law, Family Law, Property Law, and Intellectual Property. കൊല്ലത്തെ മികച്ച അഭിഭാഷകർ",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "1st Floor, Ganga Building, Opposite Kollam Collectorate",
          "addressLocality": "Kollam",
          "addressRegion": "Kerala",
          "postalCode": "691013",
          "addressCountry": "IN"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "8.892817793613391",
          "longitude": "76.57290401478547"
        },
        "telephone": "+919539650421",
        "email": "loopholelawcontact@gmail.com",
        "url": "https://loopholelaw.in/contact",
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "09:00",
            "closes": "18:00"
          }
        ],
        "priceRange": "$$",
        "areaServed": [
          {
            "@type": "City",
            "name": "Kollam"
          },
          {
            "@type": "City",
            "name": "Karunagappally"
          },
          {
            "@type": "City",
            "name": "Sasthamcotta"
          },
          {
            "@type": "City",
            "name": "Kottarakkara"
          },
          {
            "@type": "City",
            "name": "Punalur"
          }
        ],
        "knowsLanguage": ["English", "Malayalam", "Hindi"],
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Legal Services",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Criminal Law Services in Kollam"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Corporate Law Services in Kollam"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Family Law Services in Kollam"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Property Law Services in Kollam"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Intellectual Property Law in Kollam"
              }
            }
          ]
        }
      },
      {
        "@type": "Attorney",
        "@id": "https://loopholelaw.in/#attorney",
        "name": "LoopHoleLaw",
        "description": "Best lawyers in Kollam providing expert legal consultation and representation in District Court and High Court",
        "areaServed": "Kollam, Kerala, India"
      },
      {
        "@type": "Organization",
        "@id": "https://loopholelaw.in/#organization",
        "name": "LoopHoleLaw",
        "url": "https://loopholelaw.in",
        "logo": "https://loopholelaw.in/logo.png",
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+919539650421",
          "contactType": "Customer Service",
          "areaServed": "IN",
          "availableLanguage": ["English", "Malayalam", "Hindi"]
        },
        "sameAs": [
          "https://www.facebook.com/loopholelaw",
          "https://www.linkedin.com/company/loopholelaw"
        ]
      }
    ]
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send message');
      }

      // Success
      setStatus({
        type: 'success',
        message: 'Thank you for contacting us! Our legal team will respond within 24 hours.'
      });

      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        subject: '',
        message: ''
      });

    } catch (error) {
      // Error
      setStatus({
        type: 'error',
        message: error.message || 'Something went wrong. Please try again or call us directly.'
      });
    } finally {
      setIsSubmitting(false);
      // Scroll to status message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <LLSEOMeta
        title="Contact Best Lawyers in Kollam | Free Legal Consultation"
        description="Contact the best lawyers in Kollam, Kerala for expert legal consultation. Criminal, Corporate, Family & Property Law specialists. കൊല്ലത്തെ മികച്ച അഭിഭാഷകർ. Call: +91 9539650421"
        keywords="best lawyers in Kollam, lawyers near me Kollam, legal services Kollam, advocates Kollam, Kerala lawyers, Kollam district court lawyers, കൊല്ലം അഭിഭാഷകർ, criminal lawyer Kollam, family lawyer Kollam, property lawyer Kollam"
        canonicalPath="/contact"
        schema={JSON.stringify(schemaMarkup)}
      />

      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-black pt-24 pb-12">
        {/* Header Section with Location Keywords */}
        <div className="max-w-6xl mx-auto px-4 py-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Contact Best Lawyers in Kollam, Kerala
            <span className="block text-2xl md:text-3xl text-blue-400 mt-2">
              കൊല്ലത്തെ മികച്ച അഭിഭാഷകരെ ബന്ധപ്പെടുക
            </span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Serving Kollam, Karunagappally, Sasthamcotta, Kottarakkara & Punalur with expert legal services. 
            Over 7+ years of experience in Kollam District Court and Kerala High Court.
          </p>

          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center gap-6 mt-6 text-sm">
            <div className="flex items-center gap-2 text-green-400">
              <Shield className="w-5 h-5" />
              <span>7+ Years Experience</span>
            </div>
            <div className="flex items-center gap-2 text-green-400">
              <Award className="w-5 h-5" />
              <span>100+ Cases Won</span>
            </div>
            <div className="flex items-center gap-2 text-green-400">
              <Users className="w-5 h-5" />
              <span>98% Client Satisfaction</span>
            </div>
          </div>

          {/* Status Message */}
          {status.message && (
            <div className={`mt-6 mx-auto max-w-2xl p-4 rounded-lg border flex items-center gap-3 ${
              status.type === 'success' 
                ? 'bg-green-500/10 border-green-500/20 text-green-400' 
                : 'bg-red-500/10 border-red-500/20 text-red-400'
            }`}>
              {status.type === 'success' ? (
                <CheckCircle className="w-5 h-5 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
              )}
              <p>{status.message}</p>
            </div>
          )}
        </div>

        {/* Quick Contact Information Cards */}
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {/* Phone Card */}
            <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-xl border border-slate-700 hover:border-green-400 transition-all">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-lg bg-blue-500/10">
                  <Phone className="h-6 w-6 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Call Best Lawyers in Kollam</h3>
                  <p className="text-gray-400 text-sm">Mon-Fri: 9 AM - 6 PM</p>
                </div>
              </div>
              <a 
                href="tel:+919539650421" 
                className="text-green-400 hover:text-green-300 transition-colors text-xl font-semibold block"
                aria-label="Call LoopHoleLaw Kollam"
              >
                +91 9539650421
              </a>
              <p className="text-gray-500 text-sm mt-2">Free Initial Consultation</p>
            </div>

            {/* Email Card */}
            <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-xl border border-slate-700 hover:border-green-400 transition-all">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-lg bg-blue-500/10">
                  <Mail className="h-6 w-6 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Email Us</h3>
                  <p className="text-gray-400 text-sm">24 Hour Response Time</p>
                </div>
              </div>
              <a 
                href="mailto:loopholelawcontact@gmail.com" 
                className="text-green-400 hover:text-green-300 transition-colors break-all"
                aria-label="Email LoopHoleLaw"
              >
                loopholelawcontact@gmail.com
              </a>
              <p className="text-gray-500 text-sm mt-2">Get Expert Legal Advice</p>
            </div>

            {/* Office Hours Card */}
            <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-xl border border-slate-700 hover:border-green-400 transition-all">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-lg bg-blue-500/10">
                  <Clock className="h-6 w-6 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Office Hours</h3>
                  <p className="text-gray-400 text-sm">Walk-ins Welcome</p>
                </div>
              </div>
              <div className="text-green-400">
                <p className="font-semibold">Monday - Friday</p>
                <p>9:00 AM - 6:00 PM</p>
                <p className="text-gray-500 text-sm mt-2">Saturday by Appointment</p>
              </div>
            </div>
          </div>

          {/* Service Areas Section */}
          <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-xl border border-slate-700 mb-8">
            <h2 className="text-2xl font-bold text-white mb-4 text-center">
              Legal Services Across Kollam District
            </h2>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                'Kollam City', 'Karunagappally', 'Sasthamcotta', 
                'Kottarakkara', 'Punalur', 'Chavara', 
                'Paravur', 'Anchal', 'Kundara'
              ].map((area) => (
                <span 
                  key={area}
                  className="px-4 py-2 bg-blue-500/10 text-blue-400 rounded-full text-sm border border-blue-500/20"
                >
                  {area}
                </span>
              ))}
            </div>
            <p className="text-center text-gray-400 mt-4">
              Serving all areas in Kollam District with expert legal representation
            </p>
          </div>

          {/* Practice Areas Quick Links */}
          <div className="bg-gradient-to-r from-blue-500/10 to-green-500/10 border border-slate-700 rounded-xl p-6 mb-8">
            <h2 className="text-2xl font-bold text-white mb-4 text-center">
              Expert Legal Services in Kollam
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <a href="/practice-areas/criminal-law" className="text-center p-4 bg-slate-800/50 rounded-lg hover:bg-slate-700/50 transition-all border border-slate-700 hover:border-green-400">
                <h3 className="text-white font-semibold">Criminal Law</h3>
                <p className="text-sm text-gray-400 mt-1">Best Criminal Lawyers</p>
              </a>
              <a href="/practice-areas/corporate-law" className="text-center p-4 bg-slate-800/50 rounded-lg hover:bg-slate-700/50 transition-all border border-slate-700 hover:border-green-400">
                <h3 className="text-white font-semibold">Corporate Law</h3>
                <p className="text-sm text-gray-400 mt-1">Business Legal Services</p>
              </a>
              <a href="/practice-areas/family-law" className="text-center p-4 bg-slate-800/50 rounded-lg hover:bg-slate-700/50 transition-all border border-slate-700 hover:border-green-400">
                <h3 className="text-white font-semibold">Family Law</h3>
                <p className="text-sm text-gray-400 mt-1">Divorce & Custody</p>
              </a>
              <a href="/practice-areas/real-estate-law" className="text-center p-4 bg-slate-800/50 rounded-lg hover:bg-slate-700/50 transition-all border border-slate-700 hover:border-green-400">
                <h3 className="text-white font-semibold">Property Law</h3>
                <p className="text-sm text-gray-400 mt-1">Real Estate Experts</p>
              </a>
            </div>
          </div>

          {/* Map and Form Section */}
          <div className="grid md:grid-cols-2 gap-8">
            {/* Contact Form */}
            <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-xl border border-slate-700">
              <h2 className="text-2xl font-bold text-white mb-2">
                Get Free Legal Consultation
              </h2>
              <p className="text-gray-400 mb-6">
                Fill out the form and our legal experts will contact you within 24 hours
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg 
                               text-white placeholder-gray-500 focus:outline-none focus:ring-2 
                               focus:ring-green-400 focus:border-transparent"
                      placeholder="Ajay"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg 
                               text-white placeholder-gray-500 focus:outline-none focus:ring-2 
                               focus:ring-green-400 focus:border-transparent"
                      placeholder="Kumar"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg 
                             text-white placeholder-gray-500 focus:outline-none focus:ring-2 
                             focus:ring-green-400 focus:border-transparent"
                    placeholder="you@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Legal Matter / Subject *
                  </label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg 
                             text-white focus:outline-none focus:ring-2 
                             focus:ring-green-400 focus:border-transparent"
                  >
                    <option value="">Select Legal Service</option>
                    <option value="Criminal Law">Criminal Law</option>
                    <option value="Corporate Law">Corporate Law</option>
                    <option value="Family Law">Family Law / Divorce</option>
                    <option value="Property Law">Property / Real Estate Law</option>
                    <option value="Civil Law">Civil Law</option>
                    <option value="Cyber Law">Cyber Law</option>
                    <option value="Intellectual Property">Intellectual Property</option>
                    <option value="Other">Other Legal Matter</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Describe Your Legal Issue *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg 
                             text-white placeholder-gray-500 focus:outline-none focus:ring-2 
                             focus:ring-green-400 focus:border-transparent min-h-32"
                    placeholder="Please provide details about your legal matter..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-green-500 hover:bg-green-600 text-white font-medium py-3 px-4 
                           rounded-lg transition-colors duration-200 flex items-center justify-center gap-2
                           disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                      Sending Request...
                    </>
                  ) : (
                    <>
                      Get Free Consultation
                      <Mail className="h-4 w-4" />
                    </>
                  )}
                </button>
                <p className="text-xs text-gray-500 text-center">
                  By submitting this form, you agree to our privacy policy
                </p>
              </form>
            </div>

            {/* Map and Address */}
            <div className="space-y-6">
              {/* Office Address */}
              <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-xl border border-slate-700">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 rounded-lg bg-blue-500/10">
                    <MapPin className="h-6 w-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">
                      Visit Our Law Office in Kollam
                    </h3>
                    <p className="text-gray-400 text-sm">Near Kollam Collectorate</p>
                  </div>
                </div>
                <address className="text-green-400 not-italic" itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
                  <strong className="text-white">LoopHoleLaw</strong><br />
                  <span itemProp="streetAddress">1st Floor, Ganga Building<br />
                  Opposite Kollam Collectorate</span><br />
                  <span itemProp="addressLocality">Kollam</span>, 
                  <span itemProp="addressRegion"> Kerala</span> - 
                  <span itemProp="postalCode"> 691013</span><br />
                  <span itemProp="addressCountry">India</span>
                </address>
                <div className="mt-4 pt-4 border-t border-slate-700">
                  <p className="text-sm text-gray-400">
                    <strong className="text-white">Landmarks:</strong> Near Kollam District Court, 
                    Opposite Collectorate Office, Close to Asramam
                  </p>
                </div>
              </div>

              {/* Map iframe with better title */}
              <div className="aspect-video rounded-xl overflow-hidden border border-slate-700">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3941.2950242010273!2d76.57290401478547!3d8.892817793613391!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3b05fc490d8e6121%3A0x47361e7ec745a2b6!2sVIMAL%20XEROX!5e0!3m2!1sen!2sin!4v1703084488601!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Office Location"
              />
              </div>

              {/* Directions */}
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                <h4 className="text-white font-semibold mb-2">🚗 Getting Here</h4>
                <ul className="text-sm text-gray-300 space-y-1">
                  <li>• 5 min walk from Kollam District Court</li>
                  <li>• Opposite Kollam Collectorate building</li>
                  <li>• Easy parking available nearby</li>
                  <li>• Well connected by public transport</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Why Choose Us Section */}
          <div className="mt-12 bg-gradient-to-r from-slate-800/50 to-slate-700/50 backdrop-blur-sm p-8 rounded-xl border border-slate-700">
            <h2 className="text-3xl font-bold text-white mb-6 text-center">
              Why Choose LoopHoleLaw - Best Lawyers in Kollam?
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="bg-blue-500/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="text-white font-semibold mb-2">7+ Years Experience</h3>
                <p className="text-gray-400 text-sm">
                  Extensive experience in Kollam District Court and Kerala High Court
                </p>
              </div>
              <div className="text-center">
                <div className="bg-green-500/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-white font-semibold mb-2">100+ Cases Won</h3>
                <p className="text-gray-400 text-sm">
                  Proven track record of success across multiple legal domains
                </p>
              </div>
              <div className="text-center">
                <div className="bg-blue-500/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="text-white font-semibold mb-2">Multilingual Service</h3>
                <p className="text-gray-400 text-sm">
                  Fluent in English, Malayalam, and Hindi for your convenience
                </p>
              </div>
            </div>
          </div>

          {/* FAQ Section for SEO */}
          <div className="mt-12 bg-slate-800/50 backdrop-blur-sm p-8 rounded-xl border border-slate-700">
            <h2 className="text-2xl font-bold text-white mb-6">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-white font-semibold mb-2">
                  Where is LoopHoleLaw office located in Kollam?
                </h3>
                <p className="text-gray-400">
                  Our office is located at 1st Floor, Ganga Building, opposite Kollam Collectorate, 
                  Kollam, Kerala - 691013. We are easily accessible from all parts of Kollam city.
                </p>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-2">
                  What areas do you serve in Kollam district?
                </h3>
                <p className="text-gray-400">
                  We provide legal services across Kollam, Karunagappally, Sasthamcotta, Kottarakkara, 
                  Punalur, Chavara, Paravur, Anchal, and Kundara.
                </p>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-2">
                  Do you offer free initial consultation?
                </h3>
                <p className="text-gray-400">
                  Yes, we offer free initial consultation for all new clients. Call us at +91 9539650421 
                  to schedule your appointment.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LLContactPage;